import React, { useEffect, useRef } from 'react';
import AiChatCitations from './AiChatCitations';
import AiChatMessageBody from './AiChatMessageBody';
import { AI_CHAT_WELCOME_MESSAGE } from './aiChatCopy';
import './AiChatStyle.css';

/**
 * Scrollable transcript of user and assistant messages.
 * Assistant content renders a safe markdown subset (bold, lists, links);
 * user content stays plain text.
 * Empty sessions show a local welcome bubble (not persisted to the server).
 *
 * @param {Object} props
 * @param {Array<Object>} props.messages
 * @param {boolean} [props.isAwaitingReply]
 * @returns {JSX.Element}
 */
const AiChatTranscript = ({ messages = [], isAwaitingReply = false }) => {
  const bottomRef = useRef(null);
  const showToolTrace = process.env.NODE_ENV === 'development';
  const showWelcome = !messages.length && !isAwaitingReply;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isAwaitingReply, showWelcome]);

  if (showWelcome) {
    return (
      <div className="ai-chat-transcript" role="log" aria-live="polite">
        <div className="ai-chat-bubble ai-chat-bubble--assistant ai-chat-bubble--welcome">
          <AiChatMessageBody
            content={AI_CHAT_WELCOME_MESSAGE}
            role="assistant"
          />
        </div>
        <div ref={bottomRef} />
      </div>
    );
  }

  return (
    <div className="ai-chat-transcript" role="log" aria-live="polite">
      {messages.map(msg => (
        <div
          key={
            msg.id ||
            `${msg.role}-${msg.created_at}-${msg.content?.slice(0, 12)}`
          }
          className={`ai-chat-bubble ai-chat-bubble--${
            msg.role === 'user' ? 'user' : 'assistant'
          }`}
        >
          <AiChatMessageBody content={msg.content} role={msg.role} />
          {msg.role === 'assistant' &&
            Array.isArray(msg.citations) &&
            msg.citations.length > 0 && (
              <AiChatCitations citations={msg.citations} />
            )}
          {showToolTrace &&
            msg.role === 'assistant' &&
            Array.isArray(msg.tool_trace) &&
            msg.tool_trace.length > 0 && (
              <details className="ai-chat-tool-trace">
                <summary>Tool trace (dev)</summary>
                <pre>{JSON.stringify(msg.tool_trace, null, 2)}</pre>
              </details>
            )}
        </div>
      ))}
      {isAwaitingReply && (
        <div
          className="ai-chat-bubble ai-chat-bubble--assistant ai-chat-bubble__pending"
          aria-busy="true"
          aria-label="Assistant is thinking"
        >
          <span className="ai-chat-thinking">
            Thinking
            <span className="ai-chat-thinking__dots" aria-hidden="true">
              <span className="ai-chat-thinking__dot" />
              <span className="ai-chat-thinking__dot" />
              <span className="ai-chat-thinking__dot" />
            </span>
          </span>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
};

export default AiChatTranscript;
